import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
} from "../waterfall-eac/waterfallTableUtils";
import {
  calcFilterPos,
  calcOpDropdownPos,
  FILTER_PANEL_WIDTH_NUMERIC,
} from "../waterfall-eac/filterPopoverUtils";
import {
  CELL_FG,
  NEG_FG,
  EVEN_BG,
  ODD_BG,
  Z_HEAD,
  GH,
  CH,
  RH,
  TH_ROW2_TOP_BORDER,
  thCoreSC,
  tdBaseSC,
  tdFixedSC,
  tdTotalSC,
  tdTotalScrollSC,
  CARD_STYLE_SC,
  CARD_HEAD_STYLE_SC,
  CARD_TITLE_STYLE_SC,
  CARD_META_STYLE_SC,
  SC_ACCENT,
  CT_OP_ENTRIES,
  ctApplyNumFilter,
  fpBase,
  fpLbl,
  fpTxtI,
  fpNumI,
  fpOpBt,
  fpClr,
  opDropS,
  opItm,
  ctFBtnSt,
  FilterIcon,
  YoYPagination,
  buildPageNums,
  ColumnPicker,
} from "./yoyTableShared";
import { NumericFilterContent, TextFilterContent, OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";

/* ── Column definitions (no fixed widths; soft maxW only so long text wraps) ── */
const FIXED_COLS = [
  { key: "id", label: "Contract ID", align: "left", maxW: 140 },
  { key: "contractName", label: "Contract Name", align: "left", maxW: 260 },
  { key: "bu", label: "Business Unit", align: "left", maxW: 160 },
];

/* ── Metric columns (repeated for each band) ── */
const METRIC_COLS = [
  { key: "r", label: "Revenue", pct: false },
  { key: "dl", label: "Direct Labor", pct: false },
  { key: "sub", label: "Subcontractor", pct: false },
  { key: "odc", label: "ODCs", pct: false },
  { key: "tdc", label: "Total Direct Cost", pct: false },
  { key: "gp", label: "Gross Profit", pct: false },
  { key: "gm", label: "Gross Margin %", pct: true },
];

const BANDS = [{ key: "26" }, { key: "27" }, { key: "var" }];

const TH_BDR_SC = "#4a4a4c";

/* ── header helpers ── */
const thFixedBlankGroup = () => ({
  ...thCoreSC(0, GH, Z_HEAD + 1, 0, 1),
});
const thFixedCol = (col, isLast) => ({
  ...thCoreSC(GH, CH, Z_HEAD + 1, "7px 10px", 2),
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: col.align,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  ...(col.maxW != null ? { maxWidth: col.maxW } : {}),
  ...(isLast
    ? {
        boxShadow: `4px 0 8px -2px rgba(0,0,0,0.12), inset -1px 0 0 ${TH_BDR_SC}, inset 0 -1px 0 ${TH_BDR_SC}`,
        borderTop: TH_ROW2_TOP_BORDER,
      }
    : {}),
});
const thBandGroup = () => ({
  ...thCoreSC(0, GH, Z_HEAD, "7px 14px", 1),
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  textAlign: "center",
});
const thMetricCol = () => ({
  ...thCoreSC(GH, CH, Z_HEAD, "7px 8px", 2),
  fontSize: 10,
  fontWeight: 600,
  textAlign: "right",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
});

/* ── value + formatting ── */
const getVal = (row, bandKey, metricKey) => {
  if (bandKey === "var") {
    const v = row[`${metricKey}var`];
    if (v != null) return v;
    const v26 = row[`${metricKey}26`];
    const v27 = row[`${metricKey}27`];
    if (v27 != null && v26 != null) return v27 - v26;
    if (v27 != null) return v27;
    if (v26 != null) return -v26;
    return null;
  }
  return row[`${metricKey}${bandKey}`];
};

const fmtMoney = (v) => {
  if (v == null) return "";
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "$0";
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `($${abs})` : `$${abs}`;
};
const fmtMoneyVar = (v) => {
  if (v == null) return "";
  const n = Math.round(Number(v));
  if (!Number.isFinite(n) || n === 0) return "";
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `($${abs})` : `$${abs}`;
};
const fmtPct = (v) => {
  if (v == null || v === "") return "";
  const n = Math.round(Number(v) * 10) / 10;
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0.0%";
  if (n < 0) return `(${Math.abs(n).toFixed(1)}%)`;
  return `${n.toFixed(1)}%`;
};
const fmtPctVar = (v) => {
  if (v == null || v === "") return "";
  const n = Math.round(Number(v) * 10) / 10;
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0.0%";
  if (n < 0) return `(${Math.abs(n).toFixed(1)}%)`;
  return `+${n.toFixed(1)}%`;
};

const fmtCell = (v, metric, isVar) => {
  if (metric.pct) return isVar ? fmtPctVar(v) : fmtPct(v);
  return isVar ? fmtMoneyVar(v) : fmtMoney(v);
};

const isNeg = (v, pct) => {
  if (v == null || v === "") return false;
  const decimals = pct ? 1 : 0;
  const factor = 10 ** decimals;
  const rounded = Math.round(Number(v) * factor) / factor;
  return rounded < 0;
};
const cellColor = (v, pct, isBandTotal = false) => {
  if (isNeg(v, pct)) return NEG_FG;
  if (isBandTotal) return "#F59E0B";
  return CELL_FG;
};

/* ── recompute grand total when rows are filtered ── */
function recomputeTotal(rows) {
  const t = {};
  const moneyKeys = ["r", "dl", "sub", "odc", "tdc", "gp"];
  for (const yr of ["26", "27"]) {
    for (const m of moneyKeys) {
      let sum = 0;
      let any = false;
      for (const r of rows) {
        const v = r[`${m}${yr}`];
        if (v != null) {
          sum += Number(v);
          any = true;
        }
      }
      t[`${m}${yr}`] = any ? sum : null;
    }
    const rev = t[`r${yr}`];
    const gp = t[`gp${yr}`];
    t[`gm${yr}`] = rev ? (gp / rev) * 100 : 0;
  }
  for (const m of moneyKeys) {
    const v26 = t[`${m}26`];
    const v27 = t[`${m}27`];
    if (v26 != null && v27 != null) t[`${m}var`] = v27 - v26;
    else if (v27 != null) t[`${m}var`] = v27;
    else if (v26 != null) t[`${m}var`] = -v26;
    else t[`${m}var`] = null;
  }
  if (t.gm26 != null && t.gm27 != null) t.gmvar = t.gm27 - t.gm26;
  return t;
}

export default function CurrentYearToNextTab({
  rows = [],
  totalRow = {},
  fiscalYear1,
  fiscalYear2,
}) {
  const allRows = rows;
  const bandLabelOf = useCallback(
    (key) =>
      key === "26"
        ? `FY - ${fiscalYear1 ?? ""}`
        : key === "27"
          ? `FY - ${fiscalYear2 ?? ""}`
          : "Variance",
    [fiscalYear1, fiscalYear2],
  );

  const [visibleMetrics, setVisibleMetrics] = useState(
    METRIC_COLS.map((m) => m.key),
  );
  const activeMetrics = METRIC_COLS.filter((m) =>
    visibleMetrics.includes(m.key),
  );
  const toggleMetric = (key) =>
    setVisibleMetrics((prev) =>
      prev.includes(key)
        ? prev.length > 1
          ? prev.filter((k) => k !== key)
          : prev
        : [...prev, key].sort(
            (a, b) =>
              METRIC_COLS.findIndex((m) => m.key === a) -
              METRIC_COLS.findIndex((m) => m.key === b),
          ),
    );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKeys, setSortKeys] = useState([]);
  const [filterText, setFilterText] = useState({
    id: "",
    contractName: "",
    bu: "",
  });
  const [colFilters, setColFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [opPos, setOpPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  useEffect(() => {
    setPage(1);
  }, [allRows, filterText, colFilters]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      ) {
        setOpenFilterKey(null);
        setOpenOpKey(null);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    const reposition = () => {
      const fk = openFilterKeyRef.current;
      if (fk) {
        const btn = filterBtnRefs.current[fk];
        if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      }
      const ok = openOpKeyRef.current;
      if (ok) {
        const ob = opBtnRefs.current[ok];
        if (ob) setOpPos(calcOpDropdownPos(ob));
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, []);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);
  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const getSortValue = useCallback((row, colKey) => {
    const fixed = FIXED_COLS.find((c) => c.key === colKey);
    if (fixed) return row[colKey];
    const us = colKey.indexOf("_");
    if (us > 0) {
      const bKey = colKey.slice(0, us);
      const mKey = colKey.slice(us + 1);
      return getVal(row, bKey, mKey);
    }
    return row[colKey];
  }, []);

  const getSortColumnLabel = useCallback(
    (colKey) => {
      const fixed = FIXED_COLS.find((c) => c.key === colKey);
      if (fixed) return fixed.label;
      const us = colKey.indexOf("_");
      if (us > 0) {
        const bKey = colKey.slice(0, us);
        const mKey = colKey.slice(us + 1);
        const band = BANDS.find((b) => b.key === bKey);
        const metric = METRIC_COLS.find((m) => m.key === mKey);
        if (band && metric) return `${bandLabelOf(band.key)} ${metric.label}`;
      }
      return colKey;
    },
    [bandLabelOf],
  );

  const hasFilterValue = (key) => {
    if (key in filterText) return !!filterText[key];
    return !!colFilters[key]?.value;
  };

  const toggleFilter = (key, btnEl) => {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    setFilterPos(calcFilterPos(btnEl, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  const toggleOpDropdown = (key) => {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (!btn) return;
    setOpPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  };

  const renderFilterContent = (key) => {
    const fixed = FIXED_COLS.find((c) => c.key === key);
    if (fixed) {
      return (
        <TextFilterContent
          label={fixed.label}
          value={filterText[key]}
          onChange={(v) => setFilterText((p) => ({ ...p, [key]: v }))}
          onClear={() => setFilterText((p) => ({ ...p, [key]: "" }))}
        />
      );
    }
    const us = key.indexOf("_");
    const bKey = key.slice(0, us);
    const mKey = key.slice(us + 1);
    const band = BANDS.find((b) => b.key === bKey);
    const metric = METRIC_COLS.find((m) => m.key === mKey);
    const f = colFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={`${band ? bandLabelOf(band.key) : ""} · ${metric?.label}`}
        value={f.value}
        op={f.op}
        onValueChange={(v) => setColFilters((p) => ({ ...p, [key]: { ...f, value: v } }))}
        onClear={() => setColFilters((p) => { const n = { ...p }; delete n[key]; return n; })}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  };

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      for (const c of FIXED_COLS) {
        const q = filterText[c.key]?.trim().toLowerCase();
        if (q && !String(row[c.key] ?? "").toLowerCase().includes(q))
          return false;
      }
      for (const [key, f] of Object.entries(colFilters)) {
        if (!f?.value) continue;
        const us = key.indexOf("_");
        const bKey = key.slice(0, us);
        const mKey = key.slice(us + 1);
        if (!ctApplyNumFilter(getVal(row, bKey, mKey), f)) return false;
      }
      return true;
    });
  }, [allRows, filterText, colFilters]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) =>
      compareBySortKeys(a, b, sortKeys, getSortValue),
    );
  }, [filteredRows, sortKeys, getSortValue]);

  const total = sortedRows.length;
  const totPg = Math.max(1, Math.ceil(total / pageSize));
  const sp = Math.min(page, totPg);
  const paged = sortedRows.slice((sp - 1) * pageSize, sp * pageSize);
  const rs = total === 0 ? 0 : (sp - 1) * pageSize + 1;
  const re = Math.min(sp * pageSize, total);
  const pageNums = buildPageNums(totPg, sp);

  const hasTableFilters =
    FIXED_COLS.some((c) => filterText[c.key]?.trim()) ||
    Object.values(colFilters).some((f) => f?.value);

  const displayTotalRow = useMemo(() => {
    if (hasTableFilters) return recomputeTotal(sortedRows);
    return totalRow;
  }, [hasTableFilters, sortedRows, totalRow]);

  const tableStyle = {
    borderCollapse: "collapse",
    borderSpacing: 0,
    tableLayout: "auto",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 12,
    width: "max-content",
    minWidth: "100%",
  };

  return (
    <div style={CARD_STYLE_SC}>
      <div style={CARD_HEAD_STYLE_SC}>
        <div>
          <h2 style={CARD_TITLE_STYLE_SC}>
            Current Year to Next Year — FY{fiscalYear1} Vs FY{fiscalYear2}
          </h2>
          {sortKeys.length > 0 && (
            <div style={CARD_META_STYLE_SC}>
              <span style={{ color: SC_ACCENT, fontWeight: 500 }}>
                sorted by{" "}
                {sortKeys.map((s) => getSortColumnLabel(s.key)).join(", ")}
              </span>
            </div>
          )}
        </div>
        <ColumnPicker
          title="Visible Metrics"
          items={METRIC_COLS}
          visible={visibleMetrics}
          onToggle={toggleMetric}
        />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            {/* Row 1: blank fixed + band group headers */}
            <tr style={{ height: GH }}>
              <th
                colSpan={FIXED_COLS.length}
                style={thFixedBlankGroup()}
                aria-hidden
              />
              {BANDS.map((b) => (
                <th
                  key={b.key}
                  colSpan={activeMetrics.length}
                  style={thBandGroup()}
                >
                  {bandLabelOf(b.key)}
                </th>
              ))}
            </tr>

            {/* Row 2: fixed col labels (+filter) + metric column names (+filter) */}
            <tr style={{ height: CH }}>
              {FIXED_COLS.map((c, ci) => {
                const active = openFilterKey === c.key || hasFilterValue(c.key);
                return (
                  <th
                    key={c.key}
                    style={{
                      ...thFixedCol(c, ci === FIXED_COLS.length - 1),
                      cursor: "pointer",
                    }}
                    onClick={onThClick(c.key)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 4,
                        minWidth: 0,
                      }}
                    >
                      <WaterfallThLabel
                        label={c.label}
                        colKey={c.key}
                        sortKeys={sortKeys}
                        getLabel={getSortColumnLabel}
                        align={c.align}
                      />
                      <button
                        ref={(el) => {
                          filterBtnRefs.current[c.key] = el;
                        }}
                        data-filter-btn
                        style={ctFBtnSt(active)}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilter(c.key, e.currentTarget);
                        }}
                      >
                        <FilterIcon />
                      </button>
                    </div>
                  </th>
                );
              })}
              {BANDS.flatMap((b, bi) => {
                const isLastBand = bi === BANDS.length - 1;
                return activeMetrics.map((m, mi) => {
                  const colKey = `${b.key}_${m.key}`;
                  const active =
                    openFilterKey === colKey || hasFilterValue(colKey);
                  const isLastMetric = mi === activeMetrics.length - 1;
                  return (
                    <th
                      key={colKey}
                      style={{
                        ...thMetricCol(),
                        cursor: "pointer",
                        ...(!isLastBand && isLastMetric
                          ? { borderRight: `2px solid ${TH_BDR_SC}` }
                          : {}),
                      }}
                      onClick={onThClick(colKey)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 3,
                          minWidth: 0,
                        }}
                      >
                        <WaterfallThLabel
                          label={m.label}
                          colKey={colKey}
                          sortKeys={sortKeys}
                          getLabel={getSortColumnLabel}
                          align="right"
                        />
                        <button
                          ref={(el) => {
                            filterBtnRefs.current[colKey] = el;
                          }}
                          data-filter-btn
                          style={ctFBtnSt(active)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFilter(colKey, e.currentTarget);
                          }}
                        >
                          <FilterIcon />
                        </button>
                      </div>
                    </th>
                  );
                });
              })}
            </tr>
          </thead>

          <tbody>
            {/* Grand total row */}
            <tr style={{ minHeight: RH }}>
              {FIXED_COLS.map((c, ci) => (
                <td
                  key={c.key}
                  style={tdTotalSC(c, ci === FIXED_COLS.length - 1)}
                >
                  {ci === 0 ? "Total" : ""}
                </td>
              ))}
              {BANDS.flatMap((b, bi) => {
                const isLastBand = bi === BANDS.length - 1;
                  const isVar = b.key === "var";
                return activeMetrics.map((m, mi) => {
                  const isLastMetric = mi === activeMetrics.length - 1;
                  const v = getVal(displayTotalRow, b.key, m.key);
                  return (
                    <td
                      key={`tot_${b.key}_${m.key}`}
                      style={{
                        ...tdTotalScrollSC(),
                        color: cellColor(v, m.pct),
                        fontStyle: m.pct ? "italic" : "normal",
                        ...(!isLastBand && isLastMetric
                          ? { borderRight: "2px solid #A7C5A0" }
                          : {}),
                      }}
                    >
                      {fmtCell(v, m, isVar)}
                    </td>
                  );
                });
              })}
            </tr>

            {/* Data rows */}
            {paged.map((row, ri) => {
              const bg = ri % 2 === 0 ? EVEN_BG : ODD_BG;
              return (
                <tr key={row.id + ri} style={{ minHeight: RH }}>
                  {FIXED_COLS.map((c, ci) => (
                    <td
                      key={c.key}
                      style={{
                        ...tdFixedSC(
                          bg,
                          c,
                          ci === 0 ? 600 : 400,
                          ci === FIXED_COLS.length - 1,
                        ),
                        ...(c.key === "contractName"
                          ? { whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }
                          : {}),
                      }}
                      title={String(row[c.key] ?? "")}
                    >
                      {row[c.key] ?? ""}
                    </td>
                  ))}
                  {BANDS.flatMap((b, bi) => {
                    const isLastBand = bi === BANDS.length - 1;
                      const isVar = b.key === "var";
                    return activeMetrics.map((m, mi) => {
                      const isLastMetric = mi === activeMetrics.length - 1;
                      const v = getVal(row, b.key, m.key);
                      return (
                        <td
                          key={`${b.key}_${m.key}`}
                          style={{
                            ...tdBaseSC(bg),
                            color: cellColor(v, m.pct),
                            fontStyle: m.pct ? "italic" : "normal",
                            ...(!isLastBand && isLastMetric
                              ? { borderRight: "2px solid #C8D0DC" }
                              : {}),
                          }}
                        >
                          {fmtCell(v, m, isVar)}
                        </td>
                      );
                    });
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <YoYPagination
        total={total}
        rangeStart={rs}
        rangeEnd={re}
        pageSize={pageSize}
        setPageSize={setPageSize}
        page={sp}
        setPage={setPage}
        totalPages={totPg}
        pageNums={pageNums}
        unitLabel="contracts"
      />

      {/* Filter panel portal */}
      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            style={{ ...fpBase, top: filterPos.top, left: filterPos.left }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {/* Operator dropdown portal */}
      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={(colFilters[openOpKey] || { op: "gt" }).op}
            onSelect={(op) => {
              setColFilters((p) => ({
                ...p,
                [openOpKey]: { ...(p[openOpKey] || { value: "" }), op },
              }));
              setOpenOpKey(null);
            }}
            style={{ top: opPos.top, left: opPos.left }}
          />,
          document.body,
        )}
    </div>
  );
}
